import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Image,
  Animated,
} from "react-native";
import { formatDistanceToNow } from "date-fns";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppImages } from "../../../../utility/AppImages";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import Separator from "../../../../Components/Separator";
import { ScrollView } from "react-native-gesture-handler";
import ImageComponent from "../../../../Components/ImageComponent";
import { getUserNotification } from "../../../../services/notification";
import { handleApiError } from "../../../../services/errorHandler";
import { toast } from "react-toastify";
import { CropImage } from "../../../../Components/choose-avatar-components/CropImage";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import { ShowLoader } from "../../../../common/ShowLoader";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNotifications } from "../../../../context/NotificationContext";
import NotificationPopupSkeleton from "../../../../Components/skeleton-components/NotificationPopupSkeleton";
import Avatar from "../../../../Components/choose-avatar-components/Avatar";
const NotificationPopup = ({
  isVisible,
  onClose,
  position,
  accessToken,
}: {
  isVisible: boolean;
  onClose: () => void;
  position: { x: number; y: number; width: number; height: number };
  accessToken: string;
}) => {
  if (!isVisible) return null;
  const navigation = useNavigation();
  const deviceSize = useDeviceSize();
  const styles = useStyles();

  const [notifications, setNotifications] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [hasFetchedBefore, setHasFetchedBefore] = useState(null); // null = checking, false = never fetched, true = fetched before
  const [removingIds, setRemovingIds] = useState(new Set());
  const fadeAnims = useRef({});
  const heightAnims = useRef({});
  const {
    newNotification,
    refreshNotificationCounts,
    notificationCounts,
    setNewNotificationCount,
  } = useNotifications();

  useEffect(() => {
    const loadCachedAndCheckStatus = async () => {
      try {
        const isWeb = typeof window !== "undefined" && window.sessionStorage;
        let hasFetchedThisSession = false;

        if (isWeb) {
          hasFetchedThisSession =
            sessionStorage.getItem("hasFetchedNotifications") === "true";
        } else {
          hasFetchedThisSession = global.hasFetchedNotifications === true;
        }

        setHasFetchedBefore(hasFetchedThisSession);

        const cachedNotifications = await AsyncStorage.getItem(
          "cachedNotifications"
        );
        if (cachedNotifications && hasFetchedThisSession) {
          try {
            const parsed = JSON.parse(cachedNotifications);
            const validNotifications = parsed.filter(
              (notification) =>
                notification !== null && notification !== undefined
            );
            if (validNotifications.length > 0) {
              setNotifications(validNotifications);
            }
          } catch (parseError) {
            console.warn("Failed to parse cached notifications:", parseError);
          }
        }
      } catch (error) {
        console.warn("Failed to load cached data:", error);
        setHasFetchedBefore(false);
      }
    };

    loadCachedAndCheckStatus();
  }, []);

  useEffect(() => {
    if (hasFetchedBefore === null) {
      return;
    }

    if (isVisible && !hasFetchedBefore) {
      setShowLoader(true);
      getNotificationList().finally(() => {
        setShowLoader(false);
        setHasFetchedBefore(true);

        const isWeb = typeof window !== "undefined" && window.sessionStorage;
        if (isWeb) {
          sessionStorage.setItem("hasFetchedNotifications", "true");
        } else {
          global.hasFetchedNotifications = true;
        }
      });
    } else if (isVisible && hasFetchedBefore) {
      getNotificationList();
    }
  }, [isVisible, hasFetchedBefore]);

  useEffect(() => {
    if (newNotification && hasFetchedBefore) {
      getNotificationList();
    }
  }, [newNotification]);

  const removeNotification = async (id) => {
    // Optimistically update UI immediately
    const updatedNotifications = notifications.filter(
      (notification) => notification?.notificationId !== id
    );
    setNotifications(updatedNotifications);

    try {
      await AsyncStorage.setItem(
        "cachedNotifications",
        JSON.stringify(updatedNotifications)
      );
    } catch (error) {
      console.warn("Failed to update cached notifications:", error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    // Find the notification to check if it's unseen
    const notification = notifications.find(
      (n) => n?.notificationId === notificationId
    );

    // Optimistically decrease the count immediately for smooth UX
    if (notification && notification.isSeen === 0) {
      // Decrease the total notification count by 1
      setNewNotificationCount((prev) => Math.max(0, prev - 1));
    }

    // Initialize animations if not exists
    if (!fadeAnims.current[notificationId]) {
      fadeAnims.current[notificationId] = new Animated.Value(1);
    }
    if (!heightAnims.current[notificationId]) {
      heightAnims.current[notificationId] = new Animated.Value(1);
    }

    // Mark as removing
    setRemovingIds((prev) => new Set([...prev, notificationId]));

    // Animate fade out and height collapse together for smooth removal
    Animated.parallel([
      Animated.timing(fadeAnims.current[notificationId], {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(heightAnims.current[notificationId], {
        toValue: 0,
        duration: 300,
        useNativeDriver: false, // height animation doesn't support native driver
      }),
    ]).start(() => {
      // Remove from UI after animation
      removeNotification(notificationId);
      setRemovingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
      // Clean up animation refs
      delete fadeAnims.current[notificationId];
      delete heightAnims.current[notificationId];
    });

    // Call both APIs in background without showing loader
    try {
      const { base_url, endpoints } = require("../../../../Api/configs");

      // Call both mark as read and mark as seen in parallel
      const promises = [
        fetch(`${base_url}${endpoints.notification.markNotificationAsRead}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken, notificationId }),
        }),
      ];

      // Only call mark as seen if notification is unseen
      if (notification && notification.isSeen === 0) {
        promises.push(
          fetch(`${base_url}${endpoints.notification.markNotificationAsSeen}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ accessToken, notificationId }),
          })
        );
      }

      const responses = await Promise.all(promises);

      const allSuccessful = responses.every((response) => response.ok);

      // After APIs succeed, refresh to sync with actual backend counts
      if (allSuccessful) {
        // Wait a bit to ensure backend is updated
        setTimeout(() => {
          refreshNotificationCounts();
        }, 500);
      } else {
        // If APIs failed, refresh counts to revert optimistic update
        console.warn(
          "Failed to mark notification as read/seen:",
          notificationId
        );
        refreshNotificationCounts();
      }
    } catch (error) {
      // If APIs failed, refresh counts to revert optimistic update
      console.warn("Error marking notification as read/seen:", error);
      refreshNotificationCounts();
    }
  };

  const getNotificationList = async () => {
    try {
      const params = {
        accessToken,
        limit: 20,
        lastEvaluatedKey: null,
        fetchTopSix: true,
      };
      const response = await getUserNotification(params);
      const statusCode = response.statusCode;

      if (statusCode === 200 && response.body?.notifications) {
        const validNotifications = response.body.notifications
          .filter(
            (notification) =>
              notification !== null && notification !== undefined
          )
          .slice(0, 6);

        setNotifications(validNotifications);

        try {
          await AsyncStorage.setItem(
            "cachedNotifications",
            JSON.stringify(validNotifications)
          );
        } catch (cacheError) {
          console.warn("Failed to cache notifications:", cacheError);
        }
      } else {
        console.warn(
          "Failed to fetch notifications:",
          statusCode,
          response.body?.error
        );
        handleApiError(
          statusCode,
          response.body?.error || "Failed to fetch notifications"
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error(error.message || "Failed to fetch notifications");
    }
  };

  const getRelativeTime = (dateString: string, fallbackDate?: string) => {
    if (dateString) {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return formatDistanceToNow(date, { addSuffix: true });
        }
      } catch (error) {
        console.warn(
          "Error formatting primary date:",
          error,
          "dateString:",
          dateString
        );
      }
    }

    if (fallbackDate) {
      try {
        const date = new Date(fallbackDate);
        if (!isNaN(date.getTime())) {
          return formatDistanceToNow(date, { addSuffix: true });
        }
      } catch (error) {
        console.warn(
          "Error formatting fallback date:",
          error,
          "fallbackDate:",
          fallbackDate
        );
      }
    }

    return "Recently";
  };
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const getNotificationDisplayTitle = (notification) => {
    const type = notification?.type || notification?.actionType;

    // Map notification types to user-friendly display names
    const titleMap = {
      event_update_before: "Event Updated",
      event_update: "Event Update",
      event_update_status: "Event Update Status",
      challenge_request: "Challenge Request",
      challenge_response: "Challenge Response",
      "event-reminder": "Event Reminder",
      "check-in": "Check In",
      "attendance-reminder": "Attendance Reminder",
      "event-awarded": "Event Won",
      "event-lost": "Event Lost",
      "opponent-alert": "Opponent Alert",
      "audience-alert": "Audience Alert",
      "account-warning": "Account Warning",
    };

    const baseTitle = titleMap[type] || capitalizeFirstLetter(type);
    if (
      (type === "challenge_request" ||
        type === "challenge_response" ||
        type === "challenge" ||
        notification?.actionType === "challenge_request" ||
        notification?.actionType === "challenge_response") &&
      notification?.details?.eventTitle
    ) {
      return `${baseTitle} - ${notification.details.eventTitle}`;
    }
    return baseTitle;
  };

  const getNotificationImage = (notification: any) => {
    const defaultImage =
      "https://squadify-avatar.s3.eu-west-2.amazonaws.com/cat.svg";

    if (notification?.actionType === "challenge_response") {
      return (
        notification?.details?.challengerProfilePicture ||
        notification?.details?.participantProfiles?.[0]?.profilePicture ||
        defaultImage
      );
    }

    if (notification?.actionType === "challenge_request") {
      return notification?.details?.organiserProfilePicture || defaultImage;
    }

    return (
      notification?.details?.participantProfiles?.[0]?.profilePicture ||
      notification?.details?.organiserProfilePicture ||
      defaultImage
    );
  };
  return (
    <Modal transparent={true} visible={isVisible} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.overlayBackground} onPress={onClose} />
        <View
          style={[
            styles.modalContent,
            {
              position: "absolute",
              top: position.y + 10,
              ...(deviceSize === DEVICE_SIZES.XS ||
                deviceSize === DEVICE_SIZES.SM
                ? {}
                : { left: position.x - 430 }),
              width:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? "100%"
                  : 449,
            },
          ]}
        >
          {showLoader ? (
            <NotificationPopupSkeleton deviceSize={deviceSize} />
          ) : (
            <>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text style={AppFontStyle.MEDIUM_ANYBODY_18}>
                  Notifications
                </Text>
                <TouchableOpacity
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onPress={() => {
                    onClose();
                  }}
                >
                  <Image
                    source={AppImages.icCloseWhite}
                    style={{
                      height:
                        deviceSize === DEVICE_SIZES.SM ||
                          deviceSize === DEVICE_SIZES.XS
                          ? 20
                          : 25,
                      width:
                        deviceSize === DEVICE_SIZES.SM ||
                          deviceSize === DEVICE_SIZES.XS
                          ? 20
                          : 25,
                    }}
                  />
                </TouchableOpacity>
              </View>
              <Separator
                color={'#374151'}
                height={1}
                marginTop={12}
                marginBottom={12}
                marginHorizontal={0}
              />
              <View>
                {showLoader ? (
                  <View
                    style={{
                      paddingTop: 10,
                    }}
                  >
                    <ShowLoader showIndicater={showLoader} />
                  </View>
                ) : notifications.length > 0 ? (
                  (() => {
                    const unseenNotifications = notifications.filter(
                      (notification) =>
                        notification !== null &&
                        notification !== undefined &&
                        notification.isSeen === 0
                    );

                    return unseenNotifications.length > 0 ? (
                      unseenNotifications.map((notification) => {
                        // Initialize animations for this notification if not exists
                        if (!fadeAnims.current[notification?.notificationId]) {
                          fadeAnims.current[notification?.notificationId] =
                            new Animated.Value(1);
                        }
                        if (
                          !heightAnims.current[notification?.notificationId]
                        ) {
                          heightAnims.current[notification?.notificationId] =
                            new Animated.Value(1);
                        }

                        const heightInterpolate =
                          heightAnims.current[
                            notification?.notificationId
                          ]?.interpolate({
                            inputRange: [0, 1],
                            outputRange: [0, 1],
                          }) || 1;

                        return (
                          <Animated.View
                            key={notification?.notificationId}
                            style={{
                              opacity:
                                fadeAnims.current[
                                notification?.notificationId
                                ] || 1,
                              transform: [{ scaleY: heightInterpolate }],
                              overflow: "hidden",
                            }}
                          >
                            <TouchableOpacity
                              onPress={() => {
                                let selectedTab;

                                if (
                                  notification?.actionStatus === "completed"
                                ) {
                                  // Check if organiserId matches current userId for completed status

                                  selectedTab =
                                    notification?.details?.organiserId ===
                                      notification?.userId
                                      ? "Organised"
                                      : "Challenges";
                                } else {
                                  // Existing logic for non-completed statuses

                                  selectedTab =
                                    notification?.category === "organiser" ||
                                      notification?.actionType ===
                                      "event-reminder" ||
                                      notification?.actionType === "check-in"
                                      ? "Organised"
                                      : "Challenges";
                                }

                                navigateToScreen(
                                  navigation,
                                  AppScreens.NotificationScreen,
                                  {
                                    selectedTab,
                                    highlightNotificationId:
                                      notification?.notificationId,
                                  }
                                );

                                onClose();
                              }}
                              style={{
                                position: "relative",
                              }}
                            >
                              {/* Left border indicator for unseen notifications */}
                              <View
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: 4,
                                  backgroundColor: '#DC2626',
                                  borderRadius: 2,
                                }}
                              />
                              <View
                                style={{
                                  flexDirection: "row",
                                  alignItems: "flex-start",
                                  gap: 12,
                                  paddingLeft: 12,
                                }}
                              >
                                <Avatar
                                  src={getNotificationImage(notification)}
                                  variant="notificationSmallPlayer"
                                />
                                <View style={{ flex: 1 }}>
                                  <View
                                    style={{
                                      flexDirection: "row",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                    }}
                                  >
                                    <Text
                                      style={AppFontStyle.MEDIUM_ANYBODY_14}
                                    >
                                      {getNotificationDisplayTitle(
                                        notification
                                      )}
                                    </Text>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={
                                          AppFontStyle.REGULAR_ANYBODY_10
                                        }
                                      >
                                        {getRelativeTime(
                                          notification?.createdAt,
                                          notification?.updatedAt
                                        )}
                                      </Text>
                                      {/* Cross icon to mark as read */}
                                      <TouchableOpacity
                                        onPress={() =>
                                          handleMarkAsRead(
                                            notification.notificationId
                                          )
                                        }
                                        style={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: 4,
                                          backgroundColor:
                                            AppColors.gray_212222,
                                          justifyContent: "center",
                                          alignItems: "center",
                                          borderWidth: 0.5,
                                          borderColor: AppColors.gray_A6A3AA,
                                        }}
                                      >
                                        <Image
                                          source={AppImages.icClose}
                                          style={{
                                            height: 12,
                                            width: 12,
                                          }}
                                          resizeMode="contain"
                                        />
                                      </TouchableOpacity>
                                    </View>
                                  </View>
                                  {notification?.type ===
                                    "event_update_before" ||
                                    notification?.actionType ===
                                    "event_update_before" ? (
                                    <Text
                                      style={AppFontStyle.MEDIUM_ANYBODY_12}
                                    >
                                      {notification?.message}
                                    </Text>
                                  ) : notification?.actionType ===
                                    "challenge_response" &&
                                    notification?.actionStatus === "accepted" &&
                                    notification?.details?.challengerId ? (
                                    <Text
                                      style={AppFontStyle.MEDIUM_ANYBODY_12}
                                    >
                                      {notification.details.challengerId} has
                                      accepted your challenge.
                                    </Text>
                                  ) : (
                                    <Text
                                      style={AppFontStyle.MEDIUM_ANYBODY_12}
                                    >
                                      {notification?.message}
                                    </Text>
                                  )}
                                </View>
                              </View>
                            </TouchableOpacity>
                            <Separator
                              color={AppColors.gray_374151}
                              height={1}
                              marginTop={12}
                              marginBottom={12}
                              marginHorizontal={0}
                            />
                          </Animated.View>
                        );
                      })
                    ) : (
                      <View style={{ padding: 20, alignItems: "center" }}>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            {
                              color: AppColors.white,
                              textAlign: "center",
                            },
                          ]}
                        >
                          No unread notifications
                        </Text>
                      </View>
                    );
                  })()
                ) : (
                  <View style={{ padding: 20, alignItems: "center" }}>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_14,
                        { color: AppColors.white, textAlign: "center" },
                      ]}
                    >
                      No notifications yet
                    </Text>
                  </View>
                )}
              </View>

              {showLoader && (
                <View
                  style={{
                    paddingTop: 20,
                  }}
                >
                  <Separator
                    color={AppColors.white}
                    height={1}
                    marginTop={10}
                    marginBottom={10}
                    marginHorizontal={0}
                  />
                </View>
              )}
              <TouchableOpacity
                onPress={() => {
                  navigateToScreen(navigation, AppScreens.NotificationScreen);
                  onClose();
                }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_18,
                    { textAlign: "center" },
                  ]}
                >
                  View All
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    overlayBackground: {
      position: "absolute",
      width: "100%",
      height: "100%",
    },
    modalContent: {
      backgroundColor: '#111827',
      padding: 12,
      borderRadius: 12,
      elevation: 5,
      borderWidth: 1,
      borderColor: '#374151',
      shadowColor: 'rgba(0, 0, 0, 0.10)',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 1,
      shadowRadius: 10,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);

export default NotificationPopup;
